import {
  BatchLoadExperimentsData,
  Entry,
  EValidationResult,
  ValidationStatus,
} from 'business/models/Models';
import React, { useMemo } from 'react';
import { BatchloadTable, ColumnDef } from './BatchloadTable';
import { FixedTableWidth } from './utils';

interface TableProps {
  tableData: BatchLoadExperimentsData[];
  isShowingBanner?: boolean;
}

export const PreviewBatchloadDataTable = ({ tableData, isShowingBanner }: TableProps) => {
  const baseColumns: ColumnDef[] = [
    { key: 'status', title: 'Status', showStatusBorder: false, id: 'status' },
    {
      key: 'experimentNumber',
      title: 'Exp. Number',
      showStatusBorder: true,
      id: 'experimentnumber',
      textAlign: 'right',
    },
    { key: 'project', title: 'Project', showStatusBorder: true, id: 'project' },
    {
      key: 'experimentName',
      title: 'Experiment Name',
      showStatusBorder: true,
      id: 'experimentname',
    },
    {
      key: 'plateBarcode',
      title: 'Plate Barcode',
      showStatusBorder: true,
      id: 'platebarcode',
    },
    {
      key: 'plateNumber',
      title: 'Plate Number',
      showStatusBorder: true,
      id: 'platenumber',
    },
    { key: 'screen', title: 'Screen', showStatusBorder: true, id: 'screen' },
    {
      key: 'screenLotID',
      title: 'Screen Lot ID',
      showStatusBorder: true,
      id: 'screenlotid',
    },
    {
      key: 'temperature',
      title: 'Temperature',
      showStatusBorder: true,
      id: 'temperature',
    },
    { key: 'schedule', title: 'Schedule', showStatusBorder: true, id: 'schedule' },
    { key: 'ligand', title: 'Ligand', showStatusBorder: true, id: 'ligand' },
    { key: 'compound', title: 'Compound', showStatusBorder: true, id: 'compound' },
    {
      key: 'experimentNameTag',
      title: 'Experiment Name Tag',
      showStatusBorder: true,
      id: 'experimentnametag',
    },
  ];

  const maxDropCount = useMemo(
    () => Math.max(...tableData.map((row) => row.drops.length)),
    [tableData],
  );

  const columns = useMemo(() => {
    const combinedColumns = [...baseColumns];

    for (let i = 0; i < maxDropCount; i++) {
      combinedColumns.push({
        key: `protein${i + 1}`,
        title: `Protein ${i + 1}`,
        showStatusBorder: true,
        id: `protein${i + 1}`,
      });
      combinedColumns.push({
        key: `sample${i + 1}`,
        title: `Sample ${i + 1}`,
        showStatusBorder: true,
        id: `sample${i + 1}`,
      });
      combinedColumns.push({
        key: `sampleWellRatio${i + 1}`,
        title: `Sample Well Ratio ${i + 1}`,
        showStatusBorder: true,
        id: `samplewellratio${i + 1}`,
      });
      combinedColumns.push({
        key: `dropVolume${i + 1}`,
        title: `Drop Volume ${i + 1}`,
        showStatusBorder: true,
        id: `dropvolume${i + 1}`,
      });
    }

    // De-duplicate (just in case), since we move from key/value pair to a basic array
    const seen = new Set();
    const validColumns: any[] = [];

    for (const column of combinedColumns) {
      if (!seen.has(column.key)) {
        seen.add(column.key);
        validColumns.push(column);
      }
    }

    return validColumns;
  }, [baseColumns, tableData]);

  const fullTableData = useMemo<BatchLoadExperimentsData[]>(() => {
    return tableData.map((row) => {
      const validResult = new ValidationStatus({
        result: EValidationResult.Valid,
      });

      const nextRow = row;

      for (let i = 0; i < maxDropCount; i++) {
        if (row.drops[i]) {
          nextRow[`protein${i + 1}`] = row.drops[i].protein;
          nextRow[`sample${i + 1}`] = row.drops[i].sample;
          nextRow[`dropVolume${i + 1}`] = row.drops[i].dropVolume;
          nextRow[`sampleWellRatio${i + 1}`] = row.drops[i].sampleWellRatio;
        } else {
          nextRow[`protein${i + 1}`] = new Entry({ validation: validResult, value: '' });
          nextRow[`sample${i + 1}`] = new Entry({ validation: validResult, value: '' });
          nextRow[`dropVolume${i + 1}`] = new Entry({
            validation: validResult,
            value: '',
          });
          nextRow[`sampleWellRatio${i + 1}`] = new Entry({
            validation: validResult,
            value: '',
          });
        }
      }

      return nextRow;
    });
  }, [tableData, maxDropCount]);

  return (
    <BatchloadTable
      id="previewbatchloadtable"
      columns={columns}
      tableData={[...fullTableData]}
      customHeight={isShowingBanner ? '40vh' : null}
      customWidth={FixedTableWidth}
    />
  );
};
export default PreviewBatchloadDataTable;
